import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api.js';

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: true,
        retry: (count, err) => {
          if (err instanceof ApiError && ['unauthorized', 'forbidden', 'not_found', 'validation'].includes(err.kind)) return false;
          return count < 2;
        },
      },
      mutations: { retry: false },
    },
  });
}

/** Query keys are always tenant-prefixed so switching businesses never mixes data. */
export const qk = {
  me: ['me'] as const,
  settings: (t: string) => ['t', t, 'settings'] as const,
  agents: (t: string) => ['t', t, 'agents'] as const,
  members: (t: string) => ['t', t, 'members'] as const,
  connections: (t: string) => ['t', t, 'connections'] as const,
  connection: (t: string, id: string) => ['t', t, 'connections', id] as const,
  conversations: (t: string, q?: unknown) => (q === undefined ? (['t', t, 'conversations'] as const) : (['t', t, 'conversations', q] as const)),
  conversation: (t: string, id: string) => ['t', t, 'conversation', id] as const,
  messages: (t: string, id: string, episodeId?: string) => ['t', t, 'conversation', id, 'messages', episodeId ?? 'all'] as const,
  overview: (t: string, q?: unknown) => (q === undefined ? (['t', t, 'overview'] as const) : (['t', t, 'overview', q] as const)),
  calibration: (t: string) => ['t', t, 'calibration'] as const,
  drilldown: (t: string, q?: unknown) => (q === undefined ? (['t', t, 'drilldown'] as const) : (['t', t, 'drilldown', q] as const)),
  advisors: (t: string, q?: unknown) => (q === undefined ? (['t', t, 'advisors'] as const) : (['t', t, 'advisors', q] as const)),
  performance: (t: string, q?: unknown) => (q === undefined ? (['t', t, 'performance'] as const) : (['t', t, 'performance', q] as const)),
  health: (t: string) => ['t', t, 'health'] as const,
  audit: (t: string) => ['t', t, 'audit'] as const,
  projects: (t: string) => ['t', t, 'projects'] as const,
  aiRetrofit: (t: string, days?: number) => ['t', t, 'ai-retrofit', days ?? 'all'] as const,
  alerts: (t: string, q?: unknown) => (q === undefined ? (['t', t, 'alerts'] as const) : (['t', t, 'alerts', q] as const)),
  alertSummary: (t: string) => ['t', t, 'alerts-summary'] as const,
  alert: (t: string, id: string) => ['t', t, 'alert', id] as const,
  project: (t: string, id: string) => ['t', t, 'projects', id] as const,
  /** Internal (platform admins): not tenant-scoped. */
  aiUsage: (month: string) => ['admin', 'ai-usage', month] as const,
};
